import React, { useState } from 'react';
import { Solution, Suspect } from '../../types/crime-game';
import { CheckCircle, XCircle, RotateCcw, Languages, Home } from 'lucide-react';

interface ResultPanelProps {
  isWin: boolean;
  score: number;
  pointsBreakdown: { label: string; value: number }[];
  solution: Solution;
  culprit?: Suspect;
  reveal?: string;
  onPlayAgain: () => void;
  onTranslate: (text: string) => Promise<string | null>;
  translationPenalty: number;
  onGoToDashboard?: () => void;
}

export const ResultPanel: React.FC<ResultPanelProps> = ({
  isWin,
  score,
  pointsBreakdown,
  solution,
  culprit,
  reveal,
  onPlayAgain,
  onTranslate,
  translationPenalty,
  onGoToDashboard
}) => {
  const [translatedTexts, setTranslatedTexts] = useState<{
    how?: string;
    why?: string;
    key?: string;
    reveal?: string;
  }>({});
  const [translating, setTranslating] = useState<string | null>(null);

  const handleTranslate = async (field: 'how' | 'why' | 'key' | 'reveal', text: string) => {
    if (translatedTexts[field] || translating === field) return;
    setTranslating(field);
    const translation = await onTranslate(text);
    if (translation) {
      setTranslatedTexts(prev => ({ ...prev, [field]: translation }));
    }
    setTranslating(null);
  };

  return (
    <div className="w-full max-w-2xl mx-auto p-6 bg-white rounded-xl shadow-lg mt-8 text-center animate-fade-in">
      <div className="flex justify-center mb-4">
        {isWin ? (
          <CheckCircle className="w-20 h-20 text-green-500" />
        ) : (
          <XCircle className="w-20 h-20 text-red-500" />
        )}
      </div>

      <h2 className="text-3xl font-bold mb-2">
        {isWin ? 'Case Solved!' : 'The Criminal Escaped!'}
      </h2>

      <div className="text-4xl font-extrabold text-indigo-600 mb-6">
        {score > 0 ? '+' : ''}{score} Points
      </div>

      <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
        <h3 className="font-bold text-gray-700 mb-2 border-b pb-1">Score Breakdown</h3>
        <ul className="space-y-1">
          {pointsBreakdown.map((item, idx) => (
            <li key={idx} className="flex justify-between text-sm">
              <span className="text-gray-600">{item.label}</span>
              <span className={`font-mono font-bold ${item.value >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {item.value > 0 ? '+' : ''}{item.value}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-indigo-50 rounded-lg p-6 mb-8 text-left relative">
        <h3 className="font-bold text-lg text-indigo-900 mb-4">The Truth Revealed</h3>
        <div className="space-y-3">
          <p className="mb-2">
            <span className="font-bold text-indigo-900">Guilty:</span>{' '}
            <span className="text-indigo-700">{culprit?.name || 'Unknown'}</span>
          </p>
          {solution.how && (
            <div className="relative pr-12">
              <p className="text-gray-800">
                <span className="font-semibold">How:</span> {translatedTexts.how || solution.how}
              </p>
              {translatedTexts.how && (
                <p className="text-sm text-gray-500 italic mt-1">{solution.how}</p>
              )}
              {!translatedTexts.how && (
                <button
                  onClick={() => handleTranslate('how', solution.how)}
                  disabled={translating === 'how'}
                  className="absolute top-0 right-0 flex items-center gap-1 pl-3 pr-2 py-1 text-xs bg-white hover:bg-gray-100 rounded border border-gray-300 transition-colors"
                  title={`Translate (-${translationPenalty} points)`}
                >
                  <Languages className="w-3 h-3" />
                  <span>PT</span>
                </button>
              )}
            </div>
          )}
          {solution.why && (
            <div className="relative pr-12">
              <p className="text-gray-800">
                <span className="font-semibold">Why:</span> {translatedTexts.why || solution.why}
              </p>
              {translatedTexts.why && (
                <p className="text-sm text-gray-500 italic mt-1">{solution.why}</p>
              )}
              {!translatedTexts.why && (
                <button
                  onClick={() => handleTranslate('why', solution.why)}
                  disabled={translating === 'why'}
                  className="absolute top-0 right-0 flex items-center gap-1 pl-3 pr-2 py-1 text-xs bg-white hover:bg-gray-100 rounded border border-gray-300 transition-colors"
                  title={`Translate (-${translationPenalty} points)`}
                >
                  <Languages className="w-3 h-3" />
                  <span>PT</span>
                </button>
              )}
            </div>
          )}
          {solution.key && (
            <div className="relative pr-12">
              <p className="text-gray-800 mt-3 p-3 bg-white rounded border-l-4 border-indigo-500">
                <span className="font-semibold">Key Evidence:</span> {translatedTexts.key || solution.key}
              </p>
              {translatedTexts.key && (
                <p className="text-sm text-gray-500 italic mt-1 ml-3">{solution.key}</p>
              )}
              {!translatedTexts.key && (
                <button
                  onClick={() => handleTranslate('key', solution.key)}
                  disabled={translating === 'key'}
                  className="absolute top-3 right-0 flex items-center gap-1 pl-3 pr-2 py-1 text-xs bg-white hover:bg-gray-100 rounded border border-gray-300 transition-colors"
                  title={`Translate (-${translationPenalty} points)`}
                >
                  <Languages className="w-3 h-3" />
                  <span>PT</span>
                </button>
              )}
            </div>
          )}
          {reveal && (
            <div className="relative pr-12">
              <p className="text-gray-700 mt-4 italic text-center border-t pt-4">
                {translatedTexts.reveal || reveal}
              </p>
              {translatedTexts.reveal && (
                <p className="text-sm text-gray-500 italic mt-1 text-center">{reveal}</p>
              )}
              {!translatedTexts.reveal && (
                <button
                  onClick={() => handleTranslate('reveal', reveal)}
                  disabled={translating === 'reveal'}
                  className="absolute top-4 right-0 flex items-center gap-1 pl-3 pr-2 py-1 text-xs bg-white hover:bg-gray-100 rounded border border-gray-300 transition-colors"
                  title={`Translate (-${translationPenalty} points)`}
                >
                  <Languages className="w-3 h-3" />
                  <span>PT</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {isWin && (
        <div className="mb-6 text-center">
          <h3 className="text-2xl font-bold text-emerald-600 mb-2">Case Solved!</h3>
          <p className="text-lg text-gray-700">Great detective work!</p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
        {onGoToDashboard && (
          <button
            onClick={onGoToDashboard}
            className="flex items-center justify-center w-full sm:w-auto px-8 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Home className="w-4 h-4 mr-2" />
            Back to Dashboard
          </button>
        )}
        <button
          onClick={onPlayAgain}
          className="flex items-center justify-center w-full sm:w-auto px-8 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Back to Cases
        </button>
      </div>
    </div>
  );
};
